import { useEffect, useRef, useState } from "react";
import { Cloud, ArrowSquareOut } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import FloatingDialer from "@/components/FloatingDialer";
import PresenceControl from "@/components/PresenceControl";
import twilioDevice from "@/lib/twilioDevice";
import plivoDevice from "@/lib/plivoDevice";
import exotelDevice from "@/lib/exotelDevice";
import api, { setBridgeToken } from "@/lib/api";

const OPENCTI_VERSION = "62.0";

/**
 * Clean a raw caller ID into a plain phone number. Plivo/SIP callers arrive as
 * e.g. "sip:+14155551234@phone.plivo.com" or "+14155551234@domain" — we strip the
 * SIP scheme + everything after "@" so only the number (with a leading + if present)
 * is used to pre-fill the Salesforce Lead's Phone field.
 */
function normalizePhone(raw) {
    if (!raw) return "";
    let s = String(raw).trim();
    if (s.includes("@")) s = s.split("@")[0];
    s = s.replace(/^sips?:/i, "").trim();
    const hasPlus = s.startsWith("+");
    const digits = s.replace(/[^0-9]/g, "");
    if (!digits) return "";
    return (hasPlus ? "+" : "") + digits;
}

/**
 * Open CTI adapter page — loaded by Salesforce inside the Lightning utility bar.
 * Registers a click-to-dial listener that drives the Inspiro dialer, and embeds
 * the floating dialer so agents can place/receive calls without leaving Salesforce.
 */
export default function SalesforceSoftphone() {
    const { user, refresh } = useAuth();
    const [ctiStatus, setCtiStatus] = useState("loading");
    const [connecting, setConnecting] = useState(false);
    const [connectMsg, setConnectMsg] = useState("");
    const ctiRef = useRef(null);

    // Inside a Salesforce iframe the Inspiro session cookie is THIRD-PARTY and blocked
    // by the browser. Instead of cookies we use a first-party BRIDGE popup: it mints a
    // token via its own cookie and postMessages it back here (same origin as this
    // iframe), and we then authenticate every API call with that Bearer token.
    const connectPanel = () => {
        setConnecting(true);
        setConnectMsg("");
        const popup = window.open(
            `${window.location.origin}/salesforce/bridge`,
            "inspiro_sf_bridge",
            "width=460,height=640",
        );
        if (!popup) {
            setConnecting(false);
            setConnectMsg("Your browser blocked the popup. Allow popups for this site, then click Connect panel again.");
            return;
        }
        let settled = false;
        const onMsg = async (e) => {
            if (e.origin !== window.location.origin) return;
            if (e.data?.source !== "inspiro-sf-bridge" || !e.data.token) return;
            settled = true;
            window.removeEventListener("message", onMsg);
            clearInterval(poll);
            setBridgeToken(e.data.token);
            await refresh();
            setConnecting(false);
            try { popup && popup.close(); } catch (_e) { /* ignore */ }
        };
        window.addEventListener("message", onMsg);
        // If the popup closes without handing off a token, stop the spinner.
        const poll = setInterval(() => {
            if (popup && popup.closed && !settled) {
                clearInterval(poll);
                window.removeEventListener("message", onMsg);
                setConnecting(false);
                setConnectMsg("Not connected yet — click Connect panel and sign in when prompted.");
            }
        }, 800);
    };

    useEffect(() => {
        if (!user) return;
        // Open the dialer immediately so it's visible in the panel.
        twilioDevice.requestDial("");

        // Salesforce appends ?sfdcIFrameOrigin=<parent origin> to the adapter URL.
        const params = new URLSearchParams(window.location.search);
        const rawOrigin = params.get("sfdcIFrameOrigin") || params.get("sfHost");
        let sfOrigin = null;
        try {
            if (rawOrigin) sfOrigin = new URL(decodeURIComponent(rawOrigin)).origin;
            else if (document.referrer) sfOrigin = new URL(document.referrer).origin;
        } catch (_e) { /* ignore */ }

        if (!sfOrigin) { setCtiStatus("standalone"); return; }

        // Lightning uses *.lightning.force.com; Classic uses *.salesforce.com.
        const isLightning = sfOrigin.includes(".lightning.force.com");
        const libUrl = isLightning
            ? `${sfOrigin}/support/api/${OPENCTI_VERSION}/lightning/opencti_min.js`
            : `${sfOrigin}/support/api/${OPENCTI_VERSION}/interaction.js`;

        const onDial = (number) => {
            if (!number) return;
            // Click-to-dial from a Salesforce phone field: pop the softphone panel
            // open so the dialer becomes visible with the number pre-filled (matches
            // the in-app click-to-dial experience). Without this the number lands in
            // a collapsed utility-bar panel and looks like nothing happened.
            try {
                const sf = window.sforce;
                if (isLightning && sf?.opencti?.setSoftphonePanelVisibility) {
                    sf.opencti.setSoftphonePanelVisibility({ visible: true });
                } else if (!isLightning && sf?.interaction?.cti?.setVisible) {
                    sf.interaction.cti.setVisible(true);
                }
            } catch (_e) { /* ignore */ }
            twilioDevice.requestDial(number);
        };

        const wire = () => {
            try {
                if (isLightning && window.sforce && window.sforce.opencti) {
                    setCtiStatus("ready");
                    ctiRef.current = { lightning: true };
                    // Prime the visibility API during init. setSoftphonePanelVisibility
                    // is unreliable on the first inbound unless it has been invoked once
                    // at init to establish that we control the panel state — without this
                    // the panel stays minimized on incoming calls despite success:true.
                    try { window.sforce.opencti.setSoftphonePanelVisibility({ visible: false, callback: () => {} }); } catch (_e) { /* ignore */ }
                    window.sforce.opencti.enableClickToDial({ callback: () => {} });
                    window.sforce.opencti.onClickToDial({
                        listener: (p) => onDial(p?.number || p?.phoneNumber),
                    });
                } else if (!isLightning && window.sforce && window.sforce.interaction) {
                    setCtiStatus("ready");
                    ctiRef.current = { lightning: false };
                    window.sforce.interaction.cti.enableClickToDial();
                    window.sforce.interaction.cti.onClickToDial((resp) => {
                        let number = null;
                        try { number = JSON.parse(resp.result).number; } catch (_e) { /* ignore */ }
                        onDial(number);
                    });
                } else {
                    setCtiStatus("unavailable");
                }
            } catch (_e) {
                setCtiStatus("error");
            }
        };

        if (window.sforce && (window.sforce.opencti || window.sforce.interaction)) {
            wire();
            return;
        }
        const script = document.createElement("script");
        script.src = libUrl;
        script.onload = wire;
        script.onerror = () => setCtiStatus("unavailable");
        document.body.appendChild(script);
        return () => {
            try { document.body.removeChild(script); } catch (_e) { /* ignore */ }
        };
    }, [user]);

    // Inbound screen-pop: match caller → open the Salesforce record.
    // Listen on ALL provider buses (Twilio / Plivo / Exotel) so the pop fires for
    // whichever provider the workspace is on — previously only Twilio was wired, so
    // Plivo/Exotel inbound calls never popped the matching record.
    useEffect(() => {
        if (!user) return;

        // Holds the caller's number when the inbound call has NO CRM match. We do NOT
        // open the New Lead modal while ringing — only after the agent accepts the
        // call. This ref carries the (sanitized) number from `incoming` → `call:accept`.
        const pendingUnknown = { from: null };

        const openNewLeadModal = (phone) => {
            const sf = window.sforce;
            const cti = ctiRef.current;
            if (!sf || !cti || !phone) return;
            try {
                if (cti.lightning && sf.opencti) {
                    const T = sf.opencti.SCREENPOP_TYPE || {};
                    if (T.NEW_RECORD_MODAL) {
                        sf.opencti.screenPop({
                            type: T.NEW_RECORD_MODAL,
                            params: { entityName: "Lead", defaultFieldValues: { Phone: phone } },
                        });
                    } else {
                        const callType = (sf.opencti.CALL_TYPE && sf.opencti.CALL_TYPE.INBOUND) || "inbound";
                        sf.opencti.searchAndScreenPop({ searchParams: phone, queryParams: "", callType, deferred: false });
                    }
                } else if (!cti.lightning && sf.interaction) {
                    sf.interaction.searchAndScreenPop(phone, "", "inbound");
                }
            } catch (_e) { /* ignore */ }
        };

        const handler = async ({ event, from, inbound }) => {
            const cti = ctiRef.current;
            const sf = window.sforce;
            if (!cti || !sf) return;

            // When the caller hangs up (or the call ends) before the agent answers,
            // drop any pending unknown-caller pop so it never fires after the fact.
            if (event === "call:cancel" || event === "call:disconnect" || event === "call:reject" || event === "call:error") {
                pendingUnknown.from = null;
                return;
            }

            // Agent answered an inbound call → NOW open the blank New Lead modal for an
            // unknown caller, pre-filled with only the sanitized phone number.
            if (event === "call:accept") {
                if (inbound && pendingUnknown.from) {
                    openNewLeadModal(pendingUnknown.from);
                    pendingUnknown.from = null;
                }
                return;
            }

            if (event !== "incoming" || !from) return;
            // Normalize the caller ID to a clean phone number: Plivo/SIP callers arrive
            // as e.g. "sip:+14155551234@phone.plivo.com" — strip the scheme + domain so
            // the Lead's Phone field gets only the number, not the Plivo endpoint junk.
            const cleanFrom = normalizePhone(from);
            pendingUnknown.from = null;
            // Auto-open the softphone panel so the agent sees the ringing call
            // without manually clicking the utility bar item. setSoftphonePanelVisibility
            // can silently miss when the panel is minimized, so we pass a callback and
            // retry a couple of times until it reports success.
            try {
                if (cti.lightning && sf.opencti?.setSoftphonePanelVisibility) {
                    const openPanel = (attempt = 0) => {
                        try {
                            sf.opencti.setSoftphonePanelVisibility({
                                visible: true,
                                callback: (resp) => {
                                    if (!(resp && resp.success) && attempt < 3) {
                                        setTimeout(() => openPanel(attempt + 1), 500);
                                    }
                                },
                            });
                        } catch (_e) { /* ignore */ }
                    };
                    openPanel();
                } else if (!cti.lightning && sf.interaction?.cti?.setVisible) {
                    sf.interaction.cti.setVisible(true);
                }
            } catch (_e) { /* ignore */ }
            try {
                const { data } = await api.get("/integrations/salesforce/lookup", { params: { phone: cleanFrom || from } });
                if (data.matched && data.id) {
                    // Known caller → pop the matched record immediately while ringing so
                    // the agent sees who's calling before answering.
                    if (cti.lightning && sf.opencti) {
                        const T = sf.opencti.SCREENPOP_TYPE || {};
                        sf.opencti.screenPop({ type: T.SOBJECT || "sobject", params: { recordId: data.id } });
                    } else if (!cti.lightning && sf.interaction) {
                        sf.interaction.screenPop("/" + data.id, true);
                    }
                } else {
                    // Unknown caller → do NOT pop anything yet. Remember the number and
                    // open the New Lead modal only once the agent accepts the call.
                    pendingUnknown.from = cleanFrom || from;
                }
            } catch (_e) {
                // Lookup failed (network/500) → treat as unknown; still defer the New
                // Lead pop until the agent answers.
                pendingUnknown.from = cleanFrom || from;
            }
        };
        const offs = [twilioDevice.on(handler), plivoDevice.on(handler), exotelDevice.on(handler)];
        return () => offs.forEach((o) => { try { o && o(); } catch (_e) { /* ignore */ } });
    }, [user]);

    if (user === null) {
        return <div className="grid h-screen place-items-center bg-paper text-sm text-stone2">Loading…</div>;
    }

    if (!user) {
        return (
            <div className="grid h-screen place-items-center bg-paper p-6 text-center" data-testid="sf-softphone-login">
                <div className="max-w-xs space-y-3">
                    <div className="mx-auto grid h-12 w-12 place-items-center rounded-2xl bg-[#00A1E0]/10 text-[#00A1E0]">
                        <Cloud size={26} weight="fill" />
                    </div>
                    <div className="font-heading text-lg font-bold">Connect your softphone</div>
                    <p className="text-sm text-stone2">
                        Click <b>Connect panel</b> and sign in when prompted. Salesforce runs this in a secure frame, so it needs a one-time connect.
                    </p>
                    <div className="flex flex-col items-center gap-2">
                        <button
                            onClick={connectPanel}
                            disabled={connecting}
                            data-testid="sf-connect-panel"
                            className="inline-flex items-center gap-2 rounded-full bg-ink px-6 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-ink/90 disabled:opacity-60"
                        >
                            <ArrowSquareOut size={15} /> {connecting ? "Connecting…" : "Connect panel"}
                        </button>
                    </div>
                    {connectMsg && <p className="text-xs text-stone2" data-testid="sf-connect-msg">{connectMsg}</p>}
                </div>
            </div>
        );
    }

    return (
        <div className="flex h-screen flex-col bg-paper" data-testid="sf-softphone">
            <div className="border-b border-stone-200 bg-white">
                <div className="flex items-center gap-2 px-3 py-1.5">
                    <img src="/inspiro-connect-mark.png" alt="Inspiro Connect" className="h-4 w-4 rounded object-contain" />
                    <span className="font-heading text-xs font-bold">Inspiro Connect</span>
                    <span className="flex-1" />
                    <span
                        className={`inline-block h-1.5 w-1.5 rounded-full ${ctiStatus === "ready" ? "bg-emerald-500" : "bg-amber-500 animate-pulse"}`}
                    />
                    <span className="text-[9px] uppercase tracking-widest text-stone2" data-testid="sf-cti-status">
                        {ctiStatus === "ready" && "Connected"}
                        {ctiStatus === "loading" && "Init…"}
                        {ctiStatus === "standalone" && "Standalone"}
                        {(ctiStatus === "unavailable" || ctiStatus === "error") && "No CTI"}
                    </span>
                </div>
                <div className="flex items-center justify-between gap-2 border-t border-stone-100 px-3 py-1.5">
                    <div className="flex min-w-0 items-center gap-1.5">
                        <span className="grid h-5 w-5 shrink-0 place-items-center rounded-full bg-ink text-[10px] font-bold uppercase text-white">
                            {(user?.name || "?").slice(0, 1)}
                        </span>
                        <span className="truncate text-xs font-semibold" data-testid="dialer-agent-name">{user?.name}</span>
                    </div>
                    <PresenceControl compact autoReady={ctiStatus === "ready" || ctiStatus === "standalone"} />
                </div>
            </div>
            <div className="min-h-0 flex-1">
                <FloatingDialer embedded />
            </div>
        </div>
    );
}
